import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthLayout } from "../components/AuthLayout";
import { Field } from "../components/Field";
import { useAuth } from "../auth/AuthContext";
import { api, ApiRequestError } from "../api/client";

/** FR-AUTH-01 / FR-AUTH-02, DESIGN_SYSTEM.md §7. */
export function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Only same-origin relative paths are honoured, so `?next=` can't be used to
  // bounce a freshly-authenticated user to an attacker's site.
  const rawNext = params.get("next");
  const next =
    rawNext?.startsWith("/") && !rawNext.startsWith("//") ? rawNext : "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [ssoPrompt, setSsoPrompt] = useState(false);
  const [ssoSlug, setSsoSlug] = useState("");
  const [ssoError, setSsoError] = useState<string | null>(null);
  // Read from the server rather than hard-coded, so flipping ALLOW_SELF_SIGNUP
  // changes the link and the endpoint together — a visible "Create an account"
  // that leads to a 403 is worse than no link at all.
  const [selfSignupEnabled, setSelfSignupEnabled] = useState(false);

  useEffect(() => {
    void api<{ selfSignupEnabled: boolean }>("/auth/config", { auth: false })
      .then((c) => setSelfSignupEnabled(c.selfSignupEnabled))
      .catch(() => setSelfSignupEnabled(false));
  }, []);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signIn(email, password);
      navigate(next, { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : "Could not reach the server. Check your connection and try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSso(event: FormEvent) {
    event.preventDefault();
    setSsoError(null);
    try {
      await api(`/auth/sso/${encodeURIComponent(ssoSlug)}/initiate`, {
        method: "POST",
        auth: false,
      });
    } catch (caught) {
      // FR-AUTH-02 AC: "a misconfigured SSO redirect surfaces an actionable
      // error, not a generic failure." In Phase 0 that error is always
      // "not configured yet" — which is the honest answer.
      setSsoError(
        caught instanceof ApiRequestError
          ? caught.message
          : "Single sign-on is unavailable right now.",
      );
    }
  }

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to your Echo account."
      footer={
        selfSignupEnabled ? (
          <>
            New to Echo? <Link to="/signup">Create an account</Link>
          </>
        ) : (
          // Invite-only. Say who to ask rather than leaving a dead end — the
          // person hitting this screen without an account needs a next step,
          // not silence.
          <>Echo is invite-only. Ask your administrator for an invitation.</>
        )
      }
    >
      {ssoPrompt ? (
        <form onSubmit={handleSso} noValidate>
          <Field
            label="Organization identifier"
            name="organizationSlug"
            value={ssoSlug}
            onChange={(e) => setSsoSlug(e.target.value)}
            placeholder="acme-corporation"
            help="The short name your administrator gave you."
            error={ssoError ?? undefined}
            required
          />
          <button type="submit" className="btn btn--primary btn--block">
            Continue with SSO
          </button>
          <div style={{ marginTop: "var(--space-4)", textAlign: "center" }}>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => {
                setSsoPrompt(false);
                setSsoError(null);
              }}
            >
              Back to email and password
            </button>
          </div>
        </form>
      ) : (
        <>
          <button
            type="button"
            className="btn btn--secondary btn--block"
            onClick={() => setSsoPrompt(true)}
          >
            Sign in with SSO
          </button>

          <div className="auth__divider">or</div>

          <form onSubmit={handleSubmit} noValidate>
            {/* Field-level error under the form, since the API deliberately
                doesn't say which of email/password was wrong (FR-AUTH-01). */}
            {error && (
              <div className="alert alert--error" role="alert">
                {error}
              </div>
            )}

            <Field
              label="Email"
              type="email"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Field
              label="Password"
              type="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              labelAction={<Link to="/forgot-password">Forgot password?</Link>}
            />

            <button
              type="submit"
              className="btn btn--primary btn--block"
              disabled={submitting}
            >
              {submitting ? "Signing in…" : "Log in"}
            </button>
          </form>
        </>
      )}
    </AuthLayout>
  );
}
